import React, {Fragment, useCallback, useEffect, useRef} from "react";
import styled from "styled-components";
import {find, findLastIndex} from "lodash";
import Slider, {Settings} from "react-slick";
import {media} from "assets/css/media";
import {useDispatch, useSelector} from "react-redux";
import {
	getPreDrawActiveIndex,
	getPreDrawPicks,
	getQualifierGroups,
	getQualifierSquadsById,
	hasMadeFirstSelection,
} from "apps/DRAW_PREDICTOR/modules/selectors";
import {
	IPreDrawPick,
	IQualifierGroup,
	IQualifierGroupOption,
	IQualifierSquad,
} from "apps/DRAW_PREDICTOR/modules/reducers";
import {FLAGS_URL, IS_PRE_REGISTRATION} from "apps/DRAW_PREDICTOR/modules/constants";
import {PreDrawGroupType} from "apps/DRAW_PREDICTOR/modules/enums";
import binaryBg from "apps/DRAW_PREDICTOR/assets/images/predraw/binaryBg.svg";
import multipleBg from "apps/DRAW_PREDICTOR/assets/images/predraw/multipleBg.svg";
import {ReactComponent as OptionBg} from "apps/DRAW_PREDICTOR/assets/images/predraw/optionBg.svg";
import {PreRegisterCards} from "apps/DRAW_PREDICTOR/pages/preDraw/PreRegisterCards";
import {Card, Cards, DoubleTeamCard, TeamCard} from "apps/DRAW_PREDICTOR/pages/preDraw/Card";
import {ReactComponent as DoubleDisabledCardPlaceholder} from "apps/DRAW_PREDICTOR/assets/images/preDrawCardDisabledDouble.svg";
import {ReactComponent as DisabledCardPlaceholder} from "apps/DRAW_PREDICTOR/assets/images/preDrawCardDisabled.svg";
import {setPreDrawActiveIndex, updatePreDraw} from "apps/DRAW_PREDICTOR/modules/actions";
import {Exist} from "components";
import {PreDrawComingSoon} from "apps/DRAW_PREDICTOR/pages/preDraw/PreDrawComingSoon";
// import of slider styles
// eslint:disable-next-line:no-submodule-imports
import "slick-carousel/slick/slick.css";
import {trackAdobeAnalytics, useMediaQuery} from "modules";

const Wrapper = styled.div`
	position: relative;
	padding: 20px 0;
	@media (max-width: ${media.mobile}) {
		padding: 20px;
	}
`;

export const CarouselDots = styled.ul`
	position: static;
	display: flex;
	justify-content: center;
	margin-top: 27px;
	padding-bottom: 35px;
	height: 16px;

	li {
		height: 16px;
		width: 26px;
		margin: 0;

		button {
			margin: 0 auto;
			display: block;
			font-size: 0;
			height: 10px;
			width: 10px;
			background-color: rgba(255, 255, 255, 0.3);
			border-radius: 50%;
			padding: 0;
			border: none;
			outline: none;
		}

		&.slick-active button {
			background: #fff;
		}
	}
`;

const BinaryWrapper = styled.div`
	background: url(${binaryBg}) center no-repeat;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 192px;
	height: 133px;
	gap: 20px;
	background-size: cover;
	@media (max-width: ${media.mobile}) {
		gap: 10px;
		width: 165px;
		height: 115px;
	}
	@media (max-width: 375px) {
		width: 150px;
		height: 105px;
	}
	@media (max-width: 320px) {
		width: 130px;
		height: 90px;
	}
`;

const MultipleWrapper = styled.div`
	background: url(${multipleBg}) center no-repeat;
	width: 95px;
	height: 122px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-size: cover;
	@media (max-width: ${media.mobile}) {
		width: 70px;
		height: 90px;
	}

	@media (max-width: 320px) {
		width: 60px;
		height: 75px;
		&.multiple-card {
			label,
			.bg {
				width: 45px;
				height: 70px;
			}
		}
	}
`;

const RadioLabel = styled.label`
	width: 73px;
	height: 94px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	cursor: pointer;
	input {
		display: none;
		&:checked ~ div {
			.bg {
				filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.25));
				stroke-width: 2px;
				stroke: #550065;
			}
		}
	}
	.bg {
		position: absolute;
		width: 73px;
		height: 94px;
		z-index: 1;
		transition: 0.1s linear;
		@media (max-width: ${media.mobile}) {
			width: 60px;
			height: 74px;
		}
		@media (max-width: 320px) {
			width: 50px;
			height: 70px;
		}
	}
	> div > div {
		z-index: 2;
	}

	&:hover {
		.bg {
			filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.25));
			stroke-width: 2px;
			stroke: #550065;
		}
	}
	@media (max-width: ${media.mobile}) {
		width: 60px;
		height: 74px;
	}
	@media (max-width: 320px) {
		width: 50px;
		height: 70px;
	}
`;

const RadioOption = styled.div`
	height: 100%;
	width: 100%;
	> div {
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		position: relative;
		height: 100%;
		width: 100%;
	}
`;

const SquadName = styled.p`
	font-family: "Qatar2022";
	font-size: 16px;
	line-height: 160%;
	color: #000;
	font-weight: 500;
	margin-top: 5px;
	text-align: center;
`;

const Flag = styled.div`
	width: 49px;
	img {
		width: auto;
		max-width: 100%;
	}
	@media (max-width: ${media.mobile}) {
		width: 44px;
	}
	@media (max-width: 320px) {
		height: 30px;
	}
`;

const TOOLTIPS = [
	"qualifying.tooltip.group_one",
	"qualifying.tooltip.group_two",
	"qualifying.tooltip.group_three",
	"qualifying.tooltip.group_four",
];

const Groups: React.FC = () => {
	const slider = useRef<Slider>(null);
	const dispatch = useDispatch();

	const settings: Settings = {
		responsive: [
			{
				breakpoint: 5000,
				settings: "unslick",
			},
			{
				breakpoint: 768,
				settings: {
					appendDots: (dots: Element[]) => <CarouselDots>{dots}</CarouselDots>,
					arrows: false,
					dots: true,
					infinite: false,
					slidesToShow: 1,
					swipeToSlide: true,
					initialSlide: 0,
				},
			},
		],
	};
	const isMobile = useMediaQuery(`(max-width: ${media.mobile})`);
	const groups = useSelector(getQualifierGroups);
	const lastIndex = findLastIndex(groups);
	const picks = useSelector(getPreDrawPicks);
	const availableIndex = useSelector(getPreDrawActiveIndex);

	useEffect(() => {
		const allSelectedIndex = findLastIndex(picks, "allPicked");
		const newIndex = allSelectedIndex >= 0 ? allSelectedIndex + 1 : 0;
		if (newIndex > availableIndex) {
			dispatch(setPreDrawActiveIndex(newIndex));
		}

		if (isMobile && newIndex > 0 && newIndex !== availableIndex) {
			setTimeout(() => {
				slider.current?.slickNext();
			}, 1000);
		}
	}, [picks, isMobile, availableIndex, dispatch]);

	return (
		<Slider ref={slider} {...settings}>
			{groups.map((group, index) => {
				const isLast = index === lastIndex;
				if (!group.type) {
					return <Fragment key={group.id}>{null}</Fragment>;
				}
				const isDisabled = group.pre_state_enabled || availableIndex < index;
				const groupPicks = picks.find((pick) => pick.id === group.id);
				const className = group.type === PreDrawGroupType.Multiple ? "single-team" : "";

				return (
					<Card
						isDisabled={isDisabled}
						key={group.id}
						group={group}
						isLast={isLast}
						index={index}
						tooltip={TOOLTIPS[index]}>
						<Cards className={className}>
							<Questions
								isDisabled={isDisabled}
								key={group.id}
								group={group}
								groupPicks={groupPicks}
							/>
						</Cards>
					</Card>
				);
			})}
		</Slider>
	);
};

interface IGroupQuestions {
	group: IQualifierGroup;
	isDisabled: boolean;
	groupPicks?: IPreDrawPick;
}

const Questions: React.FC<IGroupQuestions> = ({group, isDisabled, groupPicks}) => {
	const squadsById = useSelector(getQualifierSquadsById);
	const isBinary = group.type === PreDrawGroupType.Binary;
	return (
		<Fragment>
			{group.questions.map(({options, id}) => {
				if (group.pre_state_enabled || isDisabled) {
					return (
						<Fragment key={id}>
							<Exist when={isBinary}>
								<DoubleTeamCard>
									<DoubleDisabledCardPlaceholder />
								</DoubleTeamCard>
							</Exist>
							<Exist when={!isBinary}>
								{options.map((option) => (
									<TeamCard key={option.id}>
										<DisabledCardPlaceholder />
									</TeamCard>
								))}
							</Exist>
						</Fragment>
					);
				}

				const picked = groupPicks && groupPicks.questions.find((pick) => pick.id === id);
				const uniq = `option-${group.id}-${id}`;

				return (
					<Fragment key={id}>
						<Exist when={isBinary}>
							<BinaryWrapper>
								{options.map((option) => {
									const squad = squadsById[option.squadId];
									const selected = Boolean(
										picked && picked.selected === option.squadId
									);

									return (
										<Option
											selected={selected}
											key={option.id}
											uniq={uniq}
											questionId={id}
											squad={squad}
											option={option}
										/>
									);
								})}
							</BinaryWrapper>
						</Exist>
						<Exist when={!isBinary}>
							{options.map((option) => {
								const squad = squadsById[option.squadId];
								const selected = Boolean(
									picked && picked.selected === option.squadId
								);

								return (
									<MultipleWrapper className="multiple-card" key={option.id}>
										<Option
											selected={selected}
											squad={squad}
											questionId={id}
											uniq={uniq}
											option={option}
										/>
									</MultipleWrapper>
								);
							})}
						</Exist>
					</Fragment>
				);
			})}
		</Fragment>
	);
};

interface IOptionProps {
	squad: IQualifierSquad;
	option: IQualifierGroupOption;
	uniq: string;
	questionId: number;
	selected: boolean;
}

const Option: React.FC<IOptionProps> = ({squad, option, uniq, questionId, selected}) => {
	const dispatch = useDispatch();
	const groups = useSelector(getPreDrawPicks);
	const firstSelectionMade = useSelector(hasMadeFirstSelection);

	const onChange = useCallback(
		(e: React.SyntheticEvent<HTMLInputElement>) => {
			if (!firstSelectionMade) {
				trackAdobeAnalytics({
					section: "",
					eventName: "drawStart",
				});
			}
			const selected = parseInt(e.currentTarget.value, 10);

			const newPicks = groups.map((group) => {
				const questions = group.questions.map((question) => {
					return {
						...question,
						selected: question.id === questionId ? selected : question.selected,
					};
				});

				return {
					...group,
					questions,
					allPicked: !find(questions, ({selected}) => selected === null),
				};
			});

			dispatch(updatePreDraw(newPicks));
		},
		[questionId, groups, dispatch, firstSelectionMade]
	);

	const id = `${uniq}-${option.id}`;
	const imageUrl = `${FLAGS_URL}${squad.shortName}.png`;

	return (
		<RadioLabel htmlFor={id}>
			<input
				type="radio"
				checked={selected}
				value={option.squadId}
				name={uniq}
				id={id}
				onChange={onChange}
			/>
			<RadioOption>
				<OptionBg className="bg" />
				<div>
					<Flag>
						<img src={imageUrl} alt={squad.name} />
					</Flag>
					<SquadName>{squad.shortName}</SquadName>
				</div>
			</RadioOption>
		</RadioLabel>
	);
};

export const PreDrawCards: React.FC = () => {
	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	});

	if (IS_PRE_REGISTRATION) {
		return (
			<Wrapper>
				<PreDrawComingSoon />
				<PreRegisterCards />
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<Groups />
		</Wrapper>
	);
};
